import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { jobApiService, Job } from '../../recruiter/services/jobApi';
import JobPostingMethodModal from '../components/JobPostingMethodModal';

/**
 * Job Postings Page
 *
 * Lists all job postings from the job-service backend
 * Features:
 * - Fetches jobs from backend API
 * - Search functionality
 * - Pagination
 * - Status badges (active/closed/draft)
 * - Static applications count and View Candidates button (not integrated yet)
 */
const JobPostings: React.FC = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<Job[]>([]);
    const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showPostJobModal, setShowPostJobModal] = useState(false);
    const jobsPerPage = 10;

    /**
     * Fetch all jobs on component mount
     */
    useEffect(() => {
        fetchJobs();
    }, []);

    /**
     * Filter jobs when search query changes
     */
    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredJobs(jobs);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = jobs.filter(
                (job) => {
                    const title = (job.title || '').toLowerCase();
                    const company = (job.companyName || '').toLowerCase();
                    const empType = (job.employmentType || '').toLowerCase();
                    const skills = job.requirements?.requiredSkills || [];

                    return (
                        title.includes(query) ||
                        company.includes(query) ||
                        empType.includes(query) ||
                        skills.some((skill) => skill.toLowerCase().includes(query))
                    );
                }
            );
            setFilteredJobs(filtered);
        }
        // Reset to first page when search changes
        setCurrentPage(1);
    }, [searchQuery, jobs]);

    /**
     * Fetch jobs for the current user from the backend
     */
    const fetchJobs = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get current user ID from localStorage
            const userDataString = localStorage.getItem('authUser');
            let userId: string | null = null;

            if (userDataString) {
                try {
                    const userData = JSON.parse(userDataString);
                    userId = userData.id || userData._id || userData.userId;
                } catch (e) {
                    console.error('Failed to parse user data:', e);
                }
            }

            // Fetch all jobs
            const { jobs: allJobs } = await jobApiService.getAllJobs({ limit: 100 });

            // Filter jobs by the current user's ID (postedBy field)
            let userJobs = allJobs;
            if (userId) {
                userJobs = allJobs.filter((job) => {
                    const postedBy = job.postedBy || job.recruiterId;
                    return postedBy === userId;
                });
                console.log(`Filtered ${allJobs.length} jobs to ${userJobs.length} for user ${userId}`);
            }

            setJobs(userJobs);
            setFilteredJobs(userJobs);
        } catch (err) {
            console.error('Failed to fetch jobs:', err);
            setError('Failed to load job postings. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Get status badge styling
     */
    const getStatusBadge = (status?: string) => {
        const statusLower = status?.toLowerCase() || 'active';
        switch (statusLower) {
            case 'active':
                return { bg: 'border-green-500', text: 'text-green-500', label: 'Active' };
            case 'closed':
                return { bg: 'border-red-500', text: 'text-red-500', label: 'Closed' };
            case 'draft':
                return { bg: 'border-gray-500', text: 'text-gray-500', label: 'Draft' };
            default:
                return { bg: 'border-green-500', text: 'text-green-500', label: 'Active' };
        }
    };

    /**
     * Calculate days remaining from closing date
     */
    const calculateDaysRemaining = (closingDate?: string): number => {
        if (!closingDate) return 30; // Default to 30 days if no closing date
        const closing = new Date(closingDate);
        const today = new Date();
        const diffTime = closing.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    /**
     * Parse salary range from string format
     */
    const parseSalaryRange = (salaryRange?: string): { min: number; max: number } => {
        if (!salaryRange) return { min: 0, max: 0 };

        // Handle formats like "$50k - $80k", "$50000-$80000", "50k-80k"
        const cleaned = salaryRange.replace(/\$/g, '').replace(/,/g, '');
        const parts = cleaned.split('-').map(s => s.trim());

        const parseAmount = (amount: string): number => {
            const num = parseFloat(amount.replace(/[^0-9.]/g, ''));
            if (amount.toLowerCase().includes('k')) {
                return num * 1000;
            }
            return num;
        };

        return {
            min: parts[0] ? parseAmount(parts[0]) : 0,
            max: parts[1] ? parseAmount(parts[1]) : 0,
        };
    };

    // Pagination calculations
    const indexOfLastJob = currentPage * jobsPerPage;
    const indexOfFirstJob = indexOfLastJob - jobsPerPage;
    const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
    const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

    /**
     * Render pagination buttons
     */
    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total pages <= 5
            for (let i = 1; i <= totalPages; i++) {
                buttons.push(
                    <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === i
                                ? 'bg-[#015FA6] text-white'
                                : 'text-[#0B111E] hover:bg-[#F4F4F4]'
                        }`}
                    >
                        {i}
                    </button>
                );
            }
        } else {
            // Show first page
            buttons.push(
                <button
                    key={1}
                    onClick={() => setCurrentPage(1)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === 1
                            ? 'bg-[#015FA6] text-white'
                            : 'text-[#0B111E] hover:bg-[#F4F4F4]'
                    }`}
                >
                    1
                </button>
            );

            // Show ellipsis if current page is far from start
            if (currentPage > 3) {
                buttons.push(
                    <span key="ellipsis-start" className="px-2 text-[#757575]">
                        ...
                    </span>
                );
            }

            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            for (let i = start; i <= end; i++) {
                buttons.push(
                    <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === i
                                ? 'bg-[#015FA6] text-white'
                                : 'text-[#0B111E] hover:bg-[#F4F4F4]'
                        }`}
                    >
                        {i}
                    </button>
                );
            }

            // Show ellipsis if current page is far from end
            if (currentPage < totalPages - 2) {
                buttons.push(
                    <span key="ellipsis-end" className="px-2 text-[#757575]">
                        ...
                    </span>
                );
            }

            // Show last page
            buttons.push(
                <button
                    key={totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === totalPages
                            ? 'bg-[#015FA6] text-white'
                            : 'text-[#0B111E] hover:bg-[#F4F4F4]'
                    }`}
                >
                    {totalPages}
                </button>
            );
        }

        return buttons;
    };

    // Loading state
    if (loading) {
        return (
            <CompanyLayout>
                <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium text-[#757575]">Loading job postings...</p>
                    </div>
                </div>
            </CompanyLayout>
        );
    }

    // Error state
    if (error) {
        return (
            <CompanyLayout>
                <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                    <div className="text-center max-w-md">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M16 8V16M16 20H16.01M28 16C28 22.6274 22.6274 28 16 28C9.37258 28 4 22.6274 4 16C4 9.37258 9.37258 4 16 4C22.6274 4 28 9.37258 28 16Z"
                                    stroke="#DC0000"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-[#0B111E] mb-2">
                            Failed to Load Job Postings
                        </h2>
                        <p className="text-base text-[#757575] mb-4">{error}</p>
                        <button
                            onClick={fetchJobs}
                            className="px-6 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </CompanyLayout>
        );
    }

    // Main view
    return (
        <CompanyLayout>
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl lg:text-[28px] font-semibold text-[#0B111E] leading-tight mb-1">
                        Job Postings
                    </h1>
                    <p className="text-sm sm:text-base font-normal text-[#515151]">
                        Manage and track your active job listings
                    </p>
                </div>
                <button
                    onClick={() => setShowPostJobModal(true)}
                    className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-[#015FA6] text-white text-sm sm:text-base font-medium rounded-lg hover:bg-[#014a85] transition-colors whitespace-nowrap"
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M10 4V16M4 10H16"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                    <span>Post new job</span>
                </button>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
                <div className="flex items-center gap-3 px-4 h-12 bg-white border border-[#EDEDED] rounded-lg max-w-full sm:max-w-[400px]">
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="9" cy="9" r="6" stroke="#757575" strokeWidth="1.5" />
                        <path
                            d="M17 17L14 14"
                            stroke="#757575"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search job postings..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 text-base font-medium text-[#0B111E] placeholder:text-[#A5A5A5] outline-none bg-transparent"
                    />
                </div>
            </div>

            {/* Jobs Table */}
            <div className="bg-white rounded-2xl p-4 sm:p-6">
                {/* Table Header - Hidden on mobile */}
                <div className="hidden md:block bg-[#F4F4F4] rounded-lg p-3 mb-2">
                    <div className="grid grid-cols-12 gap-4 text-xs font-medium text-[#515151]">
                        <div className="col-span-4">Jobs</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-2">Applications</div>
                        <div className="col-span-2">Salary</div>
                        <div className="col-span-2">Actions</div>
                    </div>
                </div>

                {/* Table Rows */}
                {currentJobs.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-lg font-medium text-[#757575]">
                            {searchQuery ? 'No jobs found matching your search' : 'No job postings yet'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-0">
                        {currentJobs.map((job, index) => {
                            const statusBadge = getStatusBadge(job.status);
                            const daysRemaining = calculateDaysRemaining(undefined);
                            const salary = parseSalaryRange(job.salaryRange);
                            // Static applications count (not integrated yet)
                            const applicationsCount = Math.floor(Math.random() * 50) + 1;

                            return (
                                <div
                                    key={job._id}
                                    className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-start md:items-center p-4 md:p-3 ${
                                        index !== currentJobs.length - 1
                                            ? 'border-b border-[#EDEDED]'
                                            : ''
                                    }`}
                                >
                                    {/* Job Title and Type */}
                                    <div className="md:col-span-4 flex flex-col gap-1">
                                        <h3
                                            className="text-sm font-semibold text-[#015FA6] hover:underline cursor-pointer"
                                            onClick={() => navigate(`/companies/jobs/${job._id || job.id}`)}
                                        >
                                            {job.title || 'Untitled Job'}
                                        </h3>
                                        <div className="flex items-center gap-1 text-xs font-normal text-[#757575]">
                                            <span>{job.employmentType || 'Full-time'}</span>
                                            <span>•</span>
                                            <span>{daysRemaining} days remaining</span>
                                        </div>
                                    </div>

                                    {/* Mobile: Status + Applications + Salary Row */}
                                    <div className="flex flex-wrap md:hidden gap-3 text-xs">
                                        <div className={`inline-flex items-center px-2 py-1 border rounded ${statusBadge.bg}`}>
                                            <span className={`font-medium ${statusBadge.text}`}>
                                                {statusBadge.label}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="8" cy="5" r="2.5" stroke="#015FA6" strokeWidth="1" />
                                                <path d="M4 12C4 9.79 5.79 8 8 8C10.21 8 12 9.79 12 12" stroke="#015FA6" strokeWidth="1" />
                                            </svg>
                                            <span className="font-normal text-[#015FA6]">{applicationsCount}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="8" cy="8" r="6" stroke="#015FA6" strokeWidth="1" />
                                                <path d="M8 5V6M8 10V11M10 7C10 6.45 9.55 6 9 6H7.5C6.95 6 6.5 6.45 6.5 7C6.5 7.55 6.95 8 7.5 8H8.5C9.05 8 9.5 8.45 9.5 9C9.5 9.55 9.05 10 8.5 10H7C6.45 10 6 9.55 6 9" stroke="#015FA6" strokeWidth="1" strokeLinecap="round" />
                                            </svg>
                                            <span className="font-normal text-[#015FA6]">
                                                {salary.min && salary.max ? `$${(salary.min / 1000).toFixed(0)}k - $${(salary.max / 1000).toFixed(0)}k` : job.salaryRange || 'Not specified'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Desktop: Status */}
                                    <div className="hidden md:block md:col-span-2">
                                        <div className={`inline-flex items-center px-3 py-1 border rounded ${statusBadge.bg}`}>
                                            <span className={`text-xs font-medium ${statusBadge.text}`}>
                                                {statusBadge.label}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Desktop: Applications (STATIC) */}
                                    <div className="hidden md:flex md:col-span-2 items-center gap-1.5">
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="8" cy="5" r="2.5" stroke="#015FA6" strokeWidth="1" />
                                            <path
                                                d="M4 12C4 9.79 5.79 8 8 8C10.21 8 12 9.79 12 12"
                                                stroke="#015FA6"
                                                strokeWidth="1"
                                            />
                                        </svg>
                                        <span className="text-xs font-normal text-[#015FA6]">
                                            {applicationsCount} Applications
                                        </span>
                                    </div>

                                    {/* Desktop: Salary */}
                                    <div className="hidden md:flex md:col-span-2 items-center gap-1.5">
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="8" cy="8" r="6" stroke="#015FA6" strokeWidth="1" />
                                            <path
                                                d="M8 5V6M8 10V11M10 7C10 6.45 9.55 6 9 6H7.5C6.95 6 6.5 6.45 6.5 7C6.5 7.55 6.95 8 7.5 8H8.5C9.05 8 9.5 8.45 9.5 9C9.5 9.55 9.05 10 8.5 10H7C6.45 10 6 9.55 6 9"
                                                stroke="#015FA6"
                                                strokeWidth="1"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                        <span className="text-xs font-normal text-[#015FA6]">
                                            {salary.min && salary.max
                                                ? `$${(salary.min / 1000).toFixed(0)}k - $${(salary.max / 1000).toFixed(0)}k`
                                                : job.salaryRange || 'Not specified'}
                                        </span>
                                    </div>

                                    {/* Actions (STATIC) */}
                                    <div className="md:col-span-2 flex items-center gap-2">
                                        <button
                                            className="px-3 py-1.5 sm:px-4 border border-[#015FA6] rounded-lg text-xs sm:text-sm font-medium text-[#015FA6] hover:bg-[#F0F7FF] transition-colors"
                                            onClick={() => navigate(`/companies/applications-review?jobId=${job._id || job.id}`)}
                                        >
                                            View Candidates
                                        </button>
                                        <button
                                            className="w-8 h-8 rounded-lg hover:bg-[#F4F4F4] flex items-center justify-center transition-colors flex-shrink-0"
                                            disabled
                                            title="More options coming soon"
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <circle cx="12" cy="6" r="1.5" fill="#757575" />
                                                <circle cx="12" cy="12" r="1.5" fill="#757575" />
                                                <circle cx="12" cy="18" r="1.5" fill="#757575" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#EDEDED]">
                        <p className="text-sm text-[#757575]">
                            Showing {indexOfFirstJob + 1} to {Math.min(indexOfLastJob, filteredJobs.length)} of{' '}
                            {filteredJobs.length} jobs
                        </p>
                        <div className="flex items-center gap-2">
                            {/* Previous Button */}
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="w-10 h-10 rounded-lg flex items-center justify-center text-[#0B111E] hover:bg-[#F4F4F4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M12 15L7 10L12 5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>

                            {/* Page Numbers */}
                            {renderPaginationButtons()}

                            {/* Next Button */}
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="w-10 h-10 rounded-lg flex items-center justify-center text-[#0B111E] hover:bg-[#F4F4F4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M8 15L13 10L8 5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <JobPostingMethodModal
                isOpen={showPostJobModal}
                onClose={() => setShowPostJobModal(false)}
            />
        </CompanyLayout>
    );
};

export default JobPostings;
